import React, { useEffect, useState } from "react";
import { AiOutlineClose } from "react-icons/ai";
import {
  getActivityTypeConfig,
  getDateRange,
  withDefaultSubtype,
  type ActivityDatePreset,
  type ActivityStatus,
  type ActivityType,
} from "../../utils/activity/filters";

/**
 * Web port of mobile's ActivityFilterSheet. The draft/apply/reset state machine
 * is copied as-is; only the container (bottom sheet → modal) and the date
 * inputs (native DatePicker → <input type="date">) differ.
 */

export interface ActivitySheetFilters {
  status?: ActivityStatus;
  startDate?: string;
  endDate?: string;
  datePreset: ActivityDatePreset;
}

export const DEFAULT_SHEET_FILTERS: ActivitySheetFilters = {
  datePreset: "all",
};

interface Props {
  open: boolean;
  value: ActivitySheetFilters;
  /** `subtype` is emitted only when a `type` (with subtypes) is provided. */
  onApply: (filters: ActivitySheetFilters, subtype?: string) => void;
  onClose: () => void;
  /**
   * When set, a "Category" (subtype) section is shown for this type. Screens
   * that manage subtype elsewhere (wallet coin activities) simply omit it.
   */
  type?: ActivityType;
  /** Current subtype for `type` — the Category section's selected value. */
  subtype?: string;
}

const STATUS_OPTIONS: { key?: ActivityStatus; label: string }[] = [
  { key: undefined, label: "All" },
  { key: "COMPLETED", label: "Completed" },
  { key: "PENDING", label: "Pending" },
  { key: "FAILED", label: "Failed" },
];

/**
 * Payouts render a failed status as "pending", so offering a "Failed" filter
 * there would contradict what the rows say. Every other type keeps it.
 */
const statusOptionsForType = (type?: ActivityType) =>
  type === "payout"
    ? STATUS_OPTIONS.filter((option) => option.key !== "FAILED")
    : STATUS_OPTIONS;

const DATE_OPTIONS: { key: ActivityDatePreset; label: string }[] = [
  { key: "all", label: "All time" },
  { key: "1w", label: "Last week" },
  { key: "1m", label: "Last month" },
  { key: "3m", label: "Last 3 months" },
  { key: "custom", label: "Custom" },
];

const OptionChip = ({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-4 py-2 rounded-full mr-2 mb-2 text-[13px] font-semibold transition-colors ${
      selected
        ? "bg-secondary_button text-white"
        : "bg-[#E8F3FF] dark:bg-[#1F2C36] text-secondary_button dark:text-gray-300"
    }`}
  >
    {label}
  </button>
);

const toInputDate = (d?: Date) => (d ? d.toISOString().slice(0, 10) : "");

const ActivityFilterPanel = ({
  open,
  value,
  onApply,
  onClose,
  type,
  subtype,
}: Props) => {
  const typeConfig = type ? getActivityTypeConfig(type) : undefined;
  const subtypeOptions = typeConfig?.subtypes ?? null;
  const statusOptions = statusOptionsForType(type);
  // Status is shared across the type tabs, so a "Failed" selection made on
  // another tab can arrive here on payout, where the option doesn't exist.
  const coerceStatus = (next?: ActivityStatus) =>
    type === "payout" && next === "FAILED" ? undefined : next;

  const [subtypeDraft, setSubtypeDraft] = useState<string | undefined>(subtype);
  const [status, setStatus] = useState<ActivityStatus | undefined>(
    coerceStatus(value.status)
  );
  const [datePreset, setDatePreset] = useState<ActivityDatePreset>(
    value.datePreset
  );
  const [customStart, setCustomStart] = useState<Date | undefined>(
    value.datePreset === "custom" && value.startDate
      ? new Date(value.startDate)
      : undefined
  );
  const [customEnd, setCustomEnd] = useState<Date | undefined>(
    value.datePreset === "custom" && value.endDate
      ? new Date(value.endDate)
      : undefined
  );

  // Re-sync draft state whenever the applied filters change externally (reset,
  // or switching the active type tab).
  useEffect(() => {
    setSubtypeDraft(subtype);
    setStatus(coerceStatus(value.status));
    setDatePreset(value.datePreset);
    setCustomStart(
      value.datePreset === "custom" && value.startDate
        ? new Date(value.startDate)
        : undefined
    );
    setCustomEnd(
      value.datePreset === "custom" && value.endDate
        ? new Date(value.endDate)
        : undefined
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, subtype]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const apply = () => {
    if (datePreset === "all") {
      onApply({ status, datePreset }, subtypeDraft);
      return;
    }
    if (datePreset === "custom") {
      onApply(
        {
          status,
          datePreset,
          startDate: customStart?.toISOString(),
          endDate: customEnd
            ? new Date(new Date(customEnd).setHours(23, 59, 59, 999)).toISOString()
            : undefined,
        },
        subtypeDraft
      );
      return;
    }
    onApply({ status, datePreset, ...getDateRange(datePreset) }, subtypeDraft);
  };

  // Reset applies immediately and closes — no second tap, matching mobile.
  const reset = () => {
    const defaultSubtype = type ? withDefaultSubtype(type).subtype : undefined;
    setSubtypeDraft(defaultSubtype);
    setStatus(undefined);
    setDatePreset("all");
    setCustomStart(undefined);
    setCustomEnd(undefined);
    onApply(DEFAULT_SHEET_FILTERS, defaultSubtype);
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="fixed inset-0 z-50 flex items-end lgss:items-center justify-center">
      <div
        className="absolute inset-0 bg-black/75"
        onClick={onClose}
        aria-hidden
      />
      <div className="relative w-full lgss:max-w-[460px] bg-white dark:bg-[#1C2227] rounded-t-2xl lgss:rounded-xl px-5 py-5 max-h-[85vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h4 className="text-[16px] font-semibold text-[#042741] dark:text-white">
            Filter transactions
          </h4>
          <button onClick={onClose} aria-label="Close filters">
            <AiOutlineClose className="text-[20px] text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {subtypeOptions && (
          <div className="mb-4">
            <h5 className="text-[13px] font-bold text-gray-500 dark:text-gray-400 mb-2">
              Category
            </h5>
            <div className="flex flex-wrap">
              {subtypeOptions.map((option) => (
                <OptionChip
                  key={option.key}
                  label={option.label}
                  selected={subtypeDraft === option.key}
                  onClick={() => setSubtypeDraft(option.key)}
                />
              ))}
            </div>
          </div>
        )}

        <div className="mb-4">
          <h5 className="text-[13px] font-bold text-gray-500 dark:text-gray-400 mb-2">
            Status
          </h5>
          <div className="flex flex-wrap">
            {statusOptions.map((option) => (
              <OptionChip
                key={option.label}
                label={option.label}
                selected={status === option.key}
                onClick={() => setStatus(option.key)}
              />
            ))}
          </div>
        </div>

        <div className="mb-4">
          <h5 className="text-[13px] font-bold text-gray-500 dark:text-gray-400 mb-2">
            Date range
          </h5>
          <div className="flex flex-wrap">
            {DATE_OPTIONS.map((option) => (
              <OptionChip
                key={option.key}
                label={option.label}
                selected={datePreset === option.key}
                onClick={() => setDatePreset(option.key)}
              />
            ))}
          </div>
        </div>

        {datePreset === "custom" && (
          <div className="flex gap-3 mb-4">
            <label className="flex-1 text-[12px] text-gray-500 dark:text-gray-400">
              From
              <input
                type="date"
                value={toInputDate(customStart)}
                max={toInputDate(customEnd) || today}
                onChange={(e) =>
                  setCustomStart(
                    e.target.value ? new Date(e.target.value) : undefined
                  )
                }
                className="w-full mt-1 px-3 py-2 rounded-lg bg-[#F1F3F5] dark:bg-[#1F2C36] text-[13px] text-[#042741] dark:text-gray-200"
              />
            </label>
            <label className="flex-1 text-[12px] text-gray-500 dark:text-gray-400">
              To
              <input
                type="date"
                value={toInputDate(customEnd)}
                min={toInputDate(customStart)}
                max={today}
                onChange={(e) =>
                  setCustomEnd(
                    e.target.value ? new Date(e.target.value) : undefined
                  )
                }
                className="w-full mt-1 px-3 py-2 rounded-lg bg-[#F1F3F5] dark:bg-[#1F2C36] text-[13px] text-[#042741] dark:text-gray-200"
              />
            </label>
          </div>
        )}

        <div className="flex gap-3 mt-6">
          <button
            onClick={reset}
            className="flex-1 py-3 rounded-xl border border-secondary_button text-secondary_button text-[14px] font-semibold"
          >
            Reset
          </button>
          <button
            onClick={apply}
            className="flex-1 py-3 rounded-xl bg-secondary_button text-white text-[14px] font-semibold"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

export default ActivityFilterPanel;
